import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

export default function QuizResult() {
    const { attemptId } = useParams();
    const [data, setData] = useState(null);
    useEffect(() => { api.get(`/attempts/${attemptId}/result`).then(r => setData(r.data)); }, [attemptId]);
    if (!data) return <Skeleton className="h-96" />;
    const { attempt, quiz, questions } = data;
    const pct = attempt.percentage;
    const donut = [{ name: "Score", value: pct }, { name: "Rest", value: 100 - pct }];

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="quiz-result-page">
            <Link to="/student/quizzes" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center"><ArrowLeft className="h-4 w-4 mr-1" /> Back to quizzes</Link>

            <div className="mx-card p-8 text-center">
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{quiz.title}</div>
                <h1 className={`font-display text-5xl lg:text-6xl mt-3 ${attempt.passed ? "text-[#16A34A]" : "text-[#E11D48]"}`}>
                    {attempt.passed ? "Passed" : "Try again"}
                </h1>
                <div className="relative h-48 mt-4">
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie data={donut} dataKey="value" startAngle={90} endAngle={-270} innerRadius={60} outerRadius={85}>
                                <Cell fill={attempt.passed ? "#16A34A" : "#E64AA1"} />
                                <Cell fill="hsl(var(--border))" />
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="font-display text-3xl">{pct}%</div>
                        <div className="text-xs text-muted-foreground">{attempt.score}/{attempt.total}</div>
                    </div>
                </div>
                <div className="text-sm text-muted-foreground mt-3">Pass mark: {quiz.pass_percentage}%</div>
            </div>

            <div>
                <h2 className="font-bold mb-3">Review answers</h2>
                <div className="space-y-3">
                    {questions.map((q, i) => {
                        const userAns = attempt.answers.find(a => a.question_id === q.id) || { selected_option_ids: [] };
                        const isCorrect = userAns.correct;
                        return (
                            <div key={q.id} className="mx-card p-5" data-testid={`result-q-${q.id}`}>
                                <div className="flex items-start gap-3">
                                    {isCorrect ? <CheckCircle2 className="h-5 w-5 text-[#16A34A] shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-[#E11D48] shrink-0 mt-0.5" />}
                                    <div className="flex-1">
                                        <div className="text-xs text-muted-foreground">Q{i + 1}</div>
                                        <div className="font-semibold mt-1">{q.text}</div>
                                        <div className="mt-3 space-y-1.5">
                                            {q.options.map(o => {
                                                const selected = userAns.selected_option_ids.includes(o.id);
                                                const isC = o.is_correct;
                                                return (
                                                    <div key={o.id} className={`text-sm px-3 py-2 rounded-lg ${isC ? "bg-[#16A34A]/10 text-[#16A34A]" : selected ? "bg-[#E11D48]/10 text-[#E11D48]" : "bg-muted"}`}>
                                                        {selected && <span className="font-bold mr-1">[Your answer]</span>}
                                                        {isC && <span className="font-bold mr-1">✓</span>}
                                                        {o.text}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
